/**
 * Client binding (security audit finding #14 — Response Replay Attack).
 *
 * Before each login (and before the forgot-password code is checked) the
 * browser picks a fresh random value. The server ties the session, or the reset
 * token, it creates to that value and checks it on every later request
 * (kvs_vsk_backend src/shared/auth/clientBinding.ts). The value is made here and
 * the server never sends it back, so a login response captured elsewhere and
 * pasted into this browser carries a session this browser cannot use.
 *
 * TRANSPORT: the `X-Client-Binding` request header, added by apiClient and
 * baseApi — the only two clients that call this backend. It used to travel as a
 * `clientBinding` cookie, but a cookie written by page script can never be
 * HttpOnly, and scanners flag it (cookie HttpOnly finding). A header carries the
 * same value with the same exposure — page script could read the cookie too —
 * without leaving a non-HttpOnly cookie in the jar.
 *
 * localStorage, not sessionStorage: sessionStorage is per tab, so a page opened
 * in a new tab would start without the binding and its session would be refused
 * once the server enforces it. localStorage is shared across tabs, as the cookie
 * was.
 */
import { randomRequestId } from './replayHeaders';

const STORAGE_KEY = 'clientBinding';
export const CLIENT_BINDING_HEADER = 'X-Client-Binding';

/** The legacy cookie, expired so it leaves browsers that still hold one. */
const expireLegacyCookie = (): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STORAGE_KEY}=; Max-Age=0; Path=/api; SameSite=Strict${secure}`;
};

const read = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // storage blocked (privacy mode, policy) — server treats it as unbound
  }
};

/** Starts a new binding. Call immediately before a request that creates a session or a reset token. */
export function rotateClientBinding(): void {
  try {
    localStorage.setItem(STORAGE_KEY, randomRequestId());
  } catch {
    // Storage blocked: the request goes out unbound, which the server logs in
    // monitor mode and refuses in enforce mode with a clear "reload" error.
  }
  expireLegacyCookie();
}

/** Drops the binding: the session it protected has ended. */
export function clearClientBinding(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing stored to remove
  }
  expireLegacyCookie();
}

/** Header carrying the current binding, or nothing when there is none. */
export function clientBindingHeaders(): Record<string, string> {
  const value = read();
  return value ? { [CLIENT_BINDING_HEADER]: value } : {};
}
